import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../context/Session';
import { DAY_KEYS, useT } from '../i18n';
import { Icon, Modal, Spinner, Switch, useToast } from '../components/ui';
import BodyAnatomyMap, { FOCUS_REGIONS, INJURY_REGIONS } from '../components/BodyAnatomyMap';
import { useExercises, useMediaQuery } from '../lib/hooks';
import { AI_KIND, allowanceBlockedMessage, allowanceFromUserData, getAiAllowance } from '../lib/allowance';
import { getProgramSuggestion } from '../lib/cloud';
import { assignNewProgram, recordAiProgramToHistory } from '../lib/services';
import { ProgramReadOnly } from './Program';

// Values are the API keys fitviz_cloud gpt_program.py accepts — same lists as
// the app's ai_trainer_page.dart.
const GOALS = [
  ['muscle gain', 'muscleGain', 'goalMuscleGainDesc', 'fitness_center'],
  ['weight loss', 'weightLoss', 'goalWeightLossDesc', 'local_fire_department'],
  ['strength', 'goalStrength', 'goalStrengthDesc', 'bolt'],
  ['general fitness', 'goalGeneralFitness', 'goalGeneralFitnessDesc', 'favorite'],
];
const LEVELS = [
  ['beginner', 'beginner', 'levelBeginnerDesc'],
  ['intermediate', 'intermediate', 'levelIntermediateDesc'],
  ['advanced', 'advanced', 'levelAdvancedDesc'],
];
const TYPES = [
  ['auto', 'programTypeAuto', 'programTypeAutoDesc', 'auto_awesome'],
  ['full-body', 'fullBody', 'fullBodyDesc', 'accessibility_new'],
  ['upper-lower', 'upperLower', 'upperLowerDesc', 'swap_vert'],
  ['push-pull-legs', 'pushPullLegs', 'pushPullLegsDesc', 'view_week'],
  ['bro-split', 'broSplit', 'broSplitDesc', 'grid_view'],
];
const EQUIPMENT = [
  ['dumbbell', 'aiEquipmentDumbbell', 'fitness_center'],
  ['barbell', 'aiEquipmentBarbell', 'horizontal_rule'],
  ['cable', 'aiEquipmentCable', 'cable'],
  ['machine', 'aiEquipmentMachine', 'precision_manufacturing'],
  ['bars', 'aiEquipmentBars', 'sports_gymnastics'],
];
const ALL_EQUIPMENT = EQUIPMENT.map(([v]) => v);
const SESSION_LENGTHS = [30, 45, 60, 90];
const DAY_PRESETS = {
  3: ['monday', 'wednesday', 'friday'],
  4: ['monday', 'tuesday', 'thursday', 'friday'],
  5: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
  6: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'],
};
const FOCUS_LABEL = {
  chest: 'focusChest',
  back: 'focusBack',
  shoulders: 'focusShoulders',
  arms: 'focusArms',
  legs: 'focusLegs',
  glutes: 'focusGlutes',
  core: 'focusCore',
};
const INJURY_LABEL = {
  neck: 'limitationNeck',
  shoulder: 'limitationShoulder',
  elbow: 'limitationElbow',
  wrist: 'limitationWrist',
  lower_back: 'limitationLowerBack',
  hip: 'limitationHip',
  knee: 'limitationKnee',
  ankle: 'limitationAnkle',
};

const FORM_KEY = 'gm_ai_trainer_form_v1';

/** Last submitted answers, so a re-roll is one click away. */
function loadForm() {
  try {
    const m = JSON.parse(localStorage.getItem(FORM_KEY) || 'null');
    if (!m || typeof m !== 'object') return null;
    const pick = (arr, allowed) => (Array.isArray(arr) ? arr.filter((x) => allowed.includes(x)) : []);
    return {
      goal: GOALS.some(([v]) => v === m.goal) ? m.goal : null,
      level: LEVELS.some(([v]) => v === m.level) ? m.level : null,
      days: pick(m.days, DAY_KEYS),
      minutes: SESSION_LENGTHS.includes(m.minutes) ? m.minutes : 60,
      equipment: Array.isArray(m.equipment) ? pick(m.equipment, ALL_EQUIPMENT) : ALL_EQUIPMENT,
      type: TYPES.some(([v]) => v === m.type) ? m.type : 'auto',
      focus: pick(m.focus, FOCUS_REGIONS),
      limitations: pick(m.limitations, INJURY_REGIONS),
      cardio: m.cardio !== false,
      note: typeof m.note === 'string' ? m.note.slice(0, 200) : '',
    };
  } catch (e) {
    return null;
  }
}

function saveForm(form) {
  try {
    localStorage.setItem(FORM_KEY, JSON.stringify(form));
  } catch (e) {
    // private mode / storage full: the form just won't be remembered
  }
}

const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

function Section({ step, title, trailing, children }) {
  return (
    <section className="gm-card gm-card--pad-lg gm-ait-section">
      <div className="gm-ait-section__head">
        <span className="gm-ait-step">{step}</span>
        <h2 className="gm-ait-section__title">{title}</h2>
        {trailing && <span className="gm-ait-trailing">{trailing}</span>}
      </div>
      {children}
    </section>
  );
}

function Pill({ selected, onClick, icon, children, block }) {
  return (
    <button
      type="button"
      className={`gm-ait-pill${selected ? ' on' : ''}${block ? ' block' : ''}`}
      aria-pressed={selected}
      onClick={onClick}
    >
      {icon && <Icon name={icon} size={17} />}
      <span>{children}</span>
    </button>
  );
}

/** Anatomy figure + the picked areas as removable pills. */
function BodyPicker({ mode, value, onChange, labels, hint, note }) {
  const t = useT();
  const order = mode === 'injury' ? INJURY_REGIONS : FOCUS_REGIONS;
  const picked = order.filter((k) => value.includes(k));
  return (
    <div>
      <div className="gm-ait-map-wrap">
        <BodyAnatomyMap
          mode={mode}
          selected={value}
          onToggle={(k) => onChange(toggle(value, k))}
          regionLabel={(k) => t(labels[k])}
          frontLabel={t('aiBodyFront')}
          backLabel={t('aiBodyBack')}
        />
      </div>
      {picked.length === 0 ? (
        <p className="gm-ait-hint"><Icon name="touch_app" size={16} />{hint}</p>
      ) : (
        <>
          <div className="gm-ait-pills center">
            {picked.map((k) => (
              <Pill key={k} selected icon="close" onClick={() => onChange(value.filter((x) => x !== k))}>
                {t(labels[k])}
              </Pill>
            ))}
            <Pill onClick={() => onChange([])}>{t('aiClearSelection')}</Pill>
          </div>
          {note && <p className="gm-ait-hint">{note}</p>}
        </>
      )}
    </div>
  );
}

/** Blocks the form while the program is generated and cycles through steps. */
function GeneratingOverlay() {
  const t = useT();
  const steps = [t('aiLoadingStep1'), t('aiLoadingStep2'), t('aiLoadingStep3')];
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((x) => x + 1), 2600);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="gm-ait-overlay" role="status" aria-live="polite">
      <div className="gm-ait-overlay__card">
        <div className="gm-ait-overlay__ring">
          <Spinner large />
          <Icon name="auto_awesome" />
        </div>
        <div className="gm-strong" style={{ fontSize: 17 }}>{t('aiLoadingTitle')}</div>
        <p key={i} className="gm-muted gm-ait-fade">{steps[i % steps.length]}</p>
      </div>
    </div>
  );
}

export default function AiTrainer() {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const { user } = useSession();
  const { byId } = useExercises();
  const saved = useMemo(loadForm, []);

  const [goal, setGoal] = useState(saved?.goal ?? null);
  const [level, setLevel] = useState(saved?.level ?? user.fitnessLevel ?? null);
  const [days, setDays] = useState(saved?.days ?? []);
  const [minutes, setMinutes] = useState(saved?.minutes ?? 60);
  const [equipment, setEquipment] = useState(saved?.equipment ?? ALL_EQUIPMENT);
  const [type, setType] = useState(saved?.type ?? 'auto');
  const [focus, setFocus] = useState(saved?.focus ?? []);
  const [limitations, setLimitations] = useState(saved?.limitations ?? []);
  const [cardio, setCardio] = useState(saved?.cardio ?? true);
  const [note, setNote] = useState(saved?.note ?? '');
  const [customizeOpen, setCustomizeOpen] = useState(!!saved?.note);

  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [accepting, setAccepting] = useState(false);

  // Wide screens put the anatomy maps in a column beside the form, at the top.
  const split = useMediaQuery('(min-width: 1100px)');

  const allowance = allowanceFromUserData(user, AI_KIND.training);
  const complete = !!(goal && level && days.length > 0);
  const fullGym = equipment.length === ALL_EQUIPMENT.length;

  const generate = async () => {
    if (!navigator.onLine) {
      toast.error(t('internetConnectionRequiredToCreate'));
      return;
    }
    const sortedDays = DAY_KEYS.filter((d) => days.includes(d));
    saveForm({ goal, level, days: sortedDays, minutes, equipment, type, focus, limitations, cardio, note });
    setBusy(true);
    try {
      const a = await getAiAllowance(user.id, AI_KIND.training);
      if (!a.canUse) {
        toast.warning(allowanceBlockedMessage(t, a), { duration: 5000 });
        return;
      }
      const result = await getProgramSuggestion({
        level,
        goal,
        programType: type,
        days: sortedDays,
        equipment: ALL_EQUIPMENT.filter((e) => equipment.includes(e)),
        sessionMinutes: minutes,
        focus: FOCUS_REGIONS.filter((f) => focus.includes(f)),
        limitations: INJURY_REGIONS.filter((l) => limitations.includes(l)),
        includeCardio: cardio,
        note,
        lang: t.lang,
        exerciseById: byId,
      });
      if (!result || !Object.keys(result.program).length) throw new Error('empty');
      // The server counted this use, accepted or not (same as the app).
      await recordAiProgramToHistory(user.id, result.program);
      setPreview(result);
    } catch (e) {
      toast.error(t('failedToGenerateProgram'));
    } finally {
      setBusy(false);
    }
  };

  const accept = async () => {
    setAccepting(true);
    try {
      await assignNewProgram(user.id, preview.program);
      setPreview(null);
      toast.success(t('aiProgramGeneratedSuccessfully'));
      navigate('/program');
    } catch (e) {
      toast.error(t('failedToCreateProgram'));
    } finally {
      setAccepting(false);
    }
  };

  const exerciseCount = preview
    ? Object.values(preview.program).reduce((sum, list) => sum + list.length, 0)
    : 0;
  const goalMeta = GOALS.find(([v]) => v === goal);
  const levelMeta = LEVELS.find(([v]) => v === level);

  const bodyMaps = (
    <>
      <Section step={7} title={t('aiFocusAreas')}>
        <BodyPicker mode="focus" value={focus} onChange={setFocus} labels={FOCUS_LABEL} hint={t('aiBodyMapHint')} />
      </Section>

      <Section step={8} title={t('aiStepInjury')}>
        <BodyPicker
          mode="injury"
          value={limitations}
          onChange={setLimitations}
          labels={INJURY_LABEL}
          hint={t('aiInjuryMapHint')}
          note={t('aiInjuryNote')}
        />
      </Section>
    </>
  );

  return (
    <div className={`gm-content gm-enter gm-ait${split ? ' gm-ait--split' : ' gm-content--narrow'}`}>
      <div className="gm-page-head" style={{ display: 'block' }}>
        <h1 className="gm-page-title">{t.upper(t('aiTrainer'))}</h1>
        <p className="gm-muted" style={{ marginTop: 4 }}>{t('aiTrainerSubtitle')}</p>
      </div>

      {allowance.isVisibleToUser && (
        <div className={`gm-banner${allowance.canUse ? '' : ' gm-banner--warn'}`} style={{ marginBottom: 14 }}>
          <Icon name="auto_awesome" size={18} />
          <span>{allowance.canUse ? t('allowanceTrainingRemaining', allowance.remaining) : allowanceBlockedMessage(t, allowance)}</span>
        </div>
      )}

      <div className="gm-ait-layout">
        <div className="gm-ait-main">
          <Section step={1} title={t('aiStepGoal')}>
            <div className="gm-ait-grid2">
              {GOALS.map(([v, label, desc, icon]) => (
                <button key={v} type="button" className={`gm-ait-tile${goal === v ? ' on' : ''}`} aria-pressed={goal === v} onClick={() => setGoal(v)}>
                  <span className="gm-ait-icon"><Icon name={icon} /></span>
                  <span className="gm-ait-tile__title">{t(label)}</span>
                  <span className="gm-ait-tile__desc">{t(desc)}</span>
                </button>
              ))}
            </div>
          </Section>

          <Section step={2} title={t('aiStepLevel')}>
            <div className="gm-ait-grid3">
              {LEVELS.map(([v, label, desc], i) => (
                <button key={v} type="button" className={`gm-ait-tile center${level === v ? ' on' : ''}`} aria-pressed={level === v} onClick={() => setLevel(v)}>
                  <span className="gm-ait-bars" aria-hidden>
                    {[0, 1, 2].map((b) => <i key={b} className={b <= i ? 'on' : ''} style={{ height: 8 + b * 5 }} />)}
                  </span>
                  <span className="gm-ait-tile__title">{t(label)}</span>
                  <span className="gm-ait-tile__desc">{t(desc)}</span>
                </button>
              ))}
            </div>
          </Section>

          <Section step={3} title={t('aiStepDays')} trailing={days.length ? t('aiDaysPerWeek', days.length) : null}>
            <div className="gm-ait-days">
              {DAY_KEYS.map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`gm-ait-day${days.includes(d) ? ' on' : ''}`}
                  aria-pressed={days.includes(d)}
                  aria-label={t(d)}
                  onClick={() => setDays(toggle(days, d))}
                >
                  {t.dayShort(d)}
                </button>
              ))}
            </div>
            <div className="gm-ait-pills" style={{ marginTop: 12 }}>
              {Object.entries(DAY_PRESETS).map(([count, preset]) => (
                <Pill
                  key={count}
                  selected={days.length === preset.length && preset.every((d) => days.includes(d))}
                  onClick={() => setDays(preset)}
                >
                  {t('aiDaysPreset', Number(count))}
                </Pill>
              ))}
            </div>
          </Section>

          <Section step={4} title={t('aiStepDuration')}>
            <div className="gm-ait-grid4">
              {SESSION_LENGTHS.map((m) => (
                <Pill key={m} block selected={minutes === m} onClick={() => setMinutes(m)}>{t('aiMinutes', m)}</Pill>
              ))}
            </div>
          </Section>

          <Section step={5} title={t('aiStepEquipment')}>
            <button
              type="button"
              className={`gm-ait-tile row${fullGym ? ' on' : ''}`}
              aria-pressed={fullGym}
              onClick={() => setEquipment(fullGym ? [] : ALL_EQUIPMENT)}
            >
              <span className="gm-ait-icon sm"><Icon name="store" /></span>
              <span className="gm-ait-tile__title gm-grow">{t('aiEquipmentFullGym')}</span>
              <span className={`gm-ait-check${fullGym ? ' on' : ''}`}>{fullGym && <Icon name="check" size={16} />}</span>
            </button>
            <div className="gm-ait-pills" style={{ marginTop: 10 }}>
              {EQUIPMENT.map(([v, label, icon]) => (
                <Pill key={v} icon={icon} selected={equipment.includes(v)} onClick={() => setEquipment(toggle(equipment, v))}>
                  {t(label)}
                </Pill>
              ))}
            </div>
            <p className="gm-ait-hint left"><Icon name="info" size={16} />{t('aiEquipmentBodyweightNote')}</p>
          </Section>

          <Section step={6} title={t('aiStepSplit')}>
            <div className="gm-stack-sm">
              {TYPES.map(([v, label, desc, icon]) => (
                <button key={v} type="button" className={`gm-ait-tile row${type === v ? ' on' : ''}`} aria-pressed={type === v} onClick={() => setType(v)}>
                  <span className="gm-ait-icon sm"><Icon name={icon} /></span>
                  <span className="gm-grow" style={{ textAlign: 'left' }}>
                    <span className="gm-ait-tile__title">
                      {t(label)}
                      {v === 'auto' && <span className="gm-ait-badge">{t('aiRecommended')}</span>}
                    </span>
                    <span className="gm-ait-tile__desc">{t(desc)}</span>
                  </span>
                  <span className={`gm-ait-check radio${type === v ? ' on' : ''}`}>{type === v && <Icon name="check" size={16} />}</span>
                </button>
              ))}
            </div>
          </Section>

          {!split && bodyMaps}

          <section className="gm-card gm-ait-section" style={{ padding: 0 }}>
            <button type="button" className="gm-ait-collapse" aria-expanded={customizeOpen} onClick={() => setCustomizeOpen((o) => !o)}>
              <span className="gm-ait-icon sm"><Icon name="tune" /></span>
              <span className="gm-grow" style={{ textAlign: 'left' }}>
                <span className="gm-ait-tile__title" style={{ fontSize: 16 }}>{t('aiStepCustomize')}</span>
                <span className="gm-ait-tile__desc">{t('aiCustomizeSubtitle')}</span>
              </span>
              <Icon name="expand_more" className={`gm-ait-chevron${customizeOpen ? ' open' : ''}`} />
            </button>
            {customizeOpen && (
              <div className="gm-ait-collapse__body">
                <div className="gm-row" style={{ alignItems: 'center' }}>
                  <div className="gm-grow">
                    <div className="gm-ait-tile__title">{t('aiIncludeCardio')}</div>
                    <div className="gm-ait-tile__desc">{t('aiIncludeCardioDesc')}</div>
                  </div>
                  <Switch checked={cardio} onChange={setCardio} label={t('aiIncludeCardio')} />
                </div>
                <div className="gm-field" style={{ marginTop: 16 }}>
                  <label htmlFor="gm-ait-note">{t('aiNoteLabel')}</label>
                  <textarea
                    id="gm-ait-note"
                    className="gm-textarea"
                    maxLength={200}
                    value={note}
                    placeholder={t('aiNoteHint')}
                    onChange={(e) => setNote(e.target.value)}
                  />
                  <span className="gm-ait-counter">{note.length}/200</span>
                </div>
              </div>
            )}
          </section>

          <button type="button" className="gm-ait-cta" onClick={generate} disabled={!complete || busy}>
            {busy ? <Spinner /> : <Icon name="auto_awesome" />}
            {busy ? t('generatingProgram') : t('generateProgram')}
          </button>
          {!complete && <p className="gm-ait-hint">{t('aiCompleteToContinue')}</p>}
        </div>
        {split && <aside className="gm-ait-aside">{bodyMaps}</aside>}
      </div>

      {busy && <GeneratingOverlay />}

      <Modal
        open={!!preview}
        wide
        onClose={accepting ? undefined : () => setPreview(null)}
        closeOnScrim={false}
        title={t('programPreview')}
        footer={(
          <>
            <span className="gm-muted gm-small gm-grow">{t('aiReplacesCurrentProgram')}</span>
            <button type="button" className="gm-btn gm-btn--ghost" onClick={() => setPreview(null)} disabled={accepting}>{t('aiDiscard')}</button>
            <button type="button" className="gm-btn" onClick={accept} disabled={accepting}>
              {accepting ? <Spinner /> : <Icon name="check" />}
              {t('aiUseProgram')}
            </button>
          </>
        )}
      >
        {preview && (
          <>
            <div className="gm-ait-pills" style={{ marginBottom: 14 }}>
              <span className="gm-ait-info"><Icon name="calendar_month" size={15} />{t('aiPreviewSummary', Object.keys(preview.program).length, exerciseCount)}</span>
              {goalMeta && <span className="gm-ait-info"><Icon name={goalMeta[3]} size={15} />{t(goalMeta[1])}</span>}
              {levelMeta && <span className="gm-ait-info"><Icon name="signal_cellular_alt" size={15} />{t(levelMeta[1])}</span>}
              <span className="gm-ait-info"><Icon name="timer" size={15} />{t('aiMinutes', minutes)}</span>
            </div>
            <ProgramReadOnly program={preview.program} titles={preview.titles} durationFirst={false} />
          </>
        )}
      </Modal>
    </div>
  );
}
